import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { brendoClient } from '../../api/brendoApi';

function disposeBokehViews(viewManager) {
  if (!viewManager || typeof viewManager[Symbol.iterator] !== 'function') return;

  for (const view of [...viewManager]) {
    const modelId = view?.model?.id;
    view?.remove?.();
    viewManager.delete?.(view);
    if (modelId && window.Bokeh?.index) {
      delete window.Bokeh.index[modelId];
    }
  }
}

export default function ExpressionDialog({ open, gene, onClose, client = brendoClient }) {
  const [status, setStatus] = useState('idle');
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!open || !gene?.id) {
      return undefined;
    }

    let cancelled = false;
    let embeddedViews = null;
    setStatus('loading');
    const plotRoot = document.getElementById('expression-plot');
    plotRoot?.replaceChildren();

    client.fetchExpression(gene.id)
      .then(async (plot) => {
        if (cancelled) return;
        if (!window.Bokeh?.embed?.embed_item) {
          throw new Error('Bokeh runtime is unavailable.');
        }
        const nextViews = await window.Bokeh.embed.embed_item(plot, 'expression-plot');
        if (cancelled) {
          disposeBokehViews(nextViews);
          return;
        }
        embeddedViews = nextViews;
        document.getElementById('expression-plot')?.querySelectorAll('a.bk-logo').forEach((link) => {
          if (!link.getAttribute('aria-label')) {
            link.setAttribute('aria-label', 'Bokeh project website');
          }
        });
        if (!cancelled) setStatus('success');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      disposeBokehViews(embeddedViews);
      plotRoot?.replaceChildren();
    };
  }, [client, gene?.id, open, retryToken]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" aria-labelledby="expression-dialog-title">
      <DialogTitle id="expression-dialog-title">
        {gene?.name || gene?.id} expression across endothelial tissues
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Points show log1p-transformed MRN-normalized counts. The table reports the untransformed Brain median and detection rate.
          </Typography>
          {status === 'loading' && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 6, justifyContent: 'center' }}>
              <CircularProgress size={24} />
              <Typography>Loading expression distribution…</Typography>
            </Stack>
          )}
          {status === 'error' && (
            <Alert
              severity="error"
              action={<Button color="inherit" aria-label="Retry expression plot" onClick={() => setRetryToken((value) => value + 1)}>Retry</Button>}
            >
              The expression distribution could not be loaded.
            </Alert>
          )}
          <Box
            role="region"
            aria-label="Scrollable expression plot"
            sx={{ width: '100%', overflowX: 'auto' }}
          >
            <Box
              id="expression-plot"
              aria-label="Expression plot"
              aria-busy={status === 'loading'}
              data-status={status}
              sx={{ width: '100%', minWidth: { xs: 900, lg: '100%' } }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
